import { Controller } from '@hotwired/stimulus'
// https://mapshaper.org/ のsimplyfy機能を使って、geojsonのサイズを小さくしている
import { prefectureGeoJSON } from './geojson/prefectureGeoJSON'

export default class extends Controller {
  static values = {
    physicalCondition: Object,
    cityCodes: Array,
    facilityGroups: Array,
    validationResult: Object,
  }

  static targets = [
    'globalLoadingOverlay',
    'mainContainer',
    'nearByCities',
    'selectedCities',
    'requestId',

    // 施設グループを算出するための質問
    'heavyMedicalCareQuestion',
    'wheelchairQuestion',
    'dementiaQuestion',
    'ageOver80Question',
    'resultFacilityGroup',

    // 選択中の市区町村
    'searchButton',

    // 条件妥当性レビュー
    'companyCountReview',
    'groupCoverageReview',

    // 条件ナビ
    'conditionNavigation',
    'recommendedRadius',
    'recommendedBudget',

    // 施設一覧
    'facilityList',

    // 詳細条件のフォーム値取得用
    'admissionFeeMin',
    'admissionFeeMax',
    'monthlyFeeMin',
    'monthlyFeeMax',
    'modifiedMonthlyFeeMin',
    'modifiedMonthlyFeeMax',
    'travelTimeMinutes',
    'residentPrefectureId',
    'residentCityId',
    'currentCareLevelId',
    'estimatedCareLevelId',
    'careInsuranceRateId',
    'residentAddress',
    'foodPlanExist',
    'welfare',
    'includeFullOccupancy',
    'includeLowMatchingScore',
    'includeHospice',
    'careLevel',
    'removeNursingCare',
    'doubleRoom',

    // エリアナビ
    'areaCenterAddress',
    'areaSearchPrefectureId',
    'areaSearchCityId',
    'areaDetailAddress',
    'showMapButton',
  ]

  // 推奨施設グループ
  facilityGroups = []

  // 自動検索実行フラグ（重複実行を防止）
  autoSearchExecuted = false

  // 市区町村データを距離ごとに分けて保持
  citiesByRange = {
    within_5km: [],
    within_10km: [],
    within_15km: [],
    within_20km: [],
    within_25km: [],
    within_30km: [],
    within_35km: [],
    within_40km: [],
  }

  companyCountByCity = {}
  selectedCities = [] // [{ jiscode, name, companyCount }]
  location = { lat: 0, lon: 0 }

  physicalCondition = {
    medicalRequired: null,
    heavyMedicalCare: null,
    wheelchair: null,
    dementia: null,
    ageOver80: null,
  }

  // 介護度定数（APIから取得）
  careLevelConstants = null

  // 施設一覧
  lastFacilityData = null

  get header () {
    const element = document.head.querySelector('meta[name="csrf-token"]')

    if (!element || !element.content) {
      console.error('CSRF token not found')
      this.showError('セキュリティトークンが見つかりません。ページを再読み込みしてください。')
      throw new Error('CSRF token not found')
    }

    return {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': element.content,
    }
  }

  get map () {
    if (this._map) return this._map

    return this.initializeMap()
  }

  get geojson () {
    if (this._geojson) return this._geojson

    this._geojson = L.geoJson(prefectureGeoJSON, {
      style: this.defaultStyle.bind(this),
      onEachFeature: this.onEachFeature.bind(this),
    })
    return this._geojson
  }

  get viewComponentClassName () {
    return 'viewcomponent::admin::ca::request::condition_matchings::new'
  }

  get validRemoveNursingCareForm () {
    const allowedPatterns = [
      { medicalRequired: true, heavyMedicalCare: false, wheelchair: false, dementia: true },
      { medicalRequired: false, wheelchair: false, dementia: true },
    ]

    return allowedPatterns.some(pattern =>
      Object.keys(pattern).every(key => this.physicalCondition[key] === pattern[key]),
    )
  }

  async connect () {
    // グローバルローディング表示
    this.showGlobalLoading()
    try {
      // 初期設定データをAPIから取得
      await this.loadInitialConfig()

      // 推奨施設グループの初期化
      this.facilityGroups = this.hasFacilityGroupsValue ? this.facilityGroupsValue : []
      console.log('Facility groups initialized:', this.facilityGroups)

      // 物理的条件の初期化
      this.physicalCondition = this.hasPhysicalConditionValue ? this.physicalConditionValue : this.physicalCondition
      console.log('Physical condition initialized:', this.physicalCondition)

      // 地図コンテナが存在する場合のみ初期化
      const mapContainer = document.getElementById('map')
      if (mapContainer) {
        this.initializeMap()
      }

      // nearByCitiesTargetが存在する場合のみイベントリスナー追加
      if (this.hasNearByCitiesTarget) {
        this.nearByCitiesTarget.addEventListener('change', this.handleCheckboxChange)
      }

      // 施設検索用月額料金を自動計算
      this.calculateModifiedMonthlyFee()

      // エリアナビの中心地を設定と地図表示ボタンの初期化
      this.updateAreaCenterAddressFromSelects()

      const centerAddress = this.getCurrentCenterAddress()
      if (!centerAddress) {
        return
      }

      this.cityCodesValue.forEach(jiscode => {
        this.selectedCities.push({ jiscode })
      })

      // 必須項目が揃っている場合は自動検索を実行
      await this.checkAndExecuteAutoSearch()
    } finally {
      // グローバルローディング非表示
      this.hideGlobalLoading()
    }
  }

  /**
   * 必須項目が揃っている場合に自動検索を実行（地図表示 → 条件検索の流れ）
   * 重複実行を防止するフラグ付き
   */
  async checkAndExecuteAutoSearch () {
    // 既に自動検索が実行済みの場合はスキップ
    if (this.autoSearchExecuted) {
      console.log('Auto search already executed, skipping')
      return
    }

    if (this.isSearchConditionReady()) {
      console.log('Auto-executing search flow: 地図を表示 → この条件で検索')

      try {
        // 実行フラグを設定（並行実行を防止）
        this.autoSearchExecuted = true

        // 1. 地図を表示
        await this.onClickMap()

        // 2. この条件で検索
        await this.onClickSearch()
      } catch (error) {
        console.error('Auto search flow failed:', error)
        // エラー時はフラグをリセット（再試行可能にする）
        this.autoSearchExecuted = false
      }
    } else {
      console.log('Auto search skipped - required conditions not met')
    }
  }

  showGlobalLoading () {
    this.globalLoadingOverlayTarget.classList.remove(`${this.viewComponentClassName}::hide`)
  }

  hideGlobalLoading () {
    this.globalLoadingOverlayTarget.classList.add(`${this.viewComponentClassName}::hide`)
  }

  // 初期設定データをAPIから取得
  async loadInitialConfig () {
    try {
      const url = `/api/admin/ca/requests/${this.requestIdTarget.value}/condition_matching_config`
      const res = await fetch(url, {
        method: 'GET',
        headers: this.header,
      })
      if (!res.ok) {
        console.warn('Failed to load initial config from API')
        return
      }
      const data = await res.json()

      // APIから取得したデータで初期値を更新
      if (data.physical_conditions && !this.hasPhysicalConditionValue) {
        this.physicalCondition = {
          medicalRequired: data.physical_conditions.medical_required,
          heavyMedicalCare: data.physical_conditions.heavy_medical_care,
          wheelchair: data.physical_conditions.wheelchair_required,
          dementia: data.physical_conditions.dementia_exists,
          ageOver80: data.physical_conditions.age_over_80,
        }
      }

      if (data.recommended_facility_groups && !this.hasFacilityGroupsValue) {
        this.facilityGroups = data.recommended_facility_groups
      }

      if (data.care_level_constants) {
        this.careLevelConstants = data.care_level_constants
      }
    } catch (error) {
      console.warn('Error loading initial config:', error)
    }
  }

  /**
   * エリアナビ機能
   */
  initializeMap () {
    try {
      const mapContainer = document.getElementById('map')
      if (!mapContainer) {
        console.warn('Map container not found')
        return null
      }

      const map = L.map('map')
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> | <a href="https://nlftp.mlit.go.jp/ksj/gml/datalist/KsjTmplt-N03-2024.html">国土数値情報（行政区域データ）（国土交通省）</a> を加工して作成',
      }).addTo(map)

      this.addLegend(map)

      this._map = map
      return map
    } catch (error) {
      console.error('Failed to initialize map:', error)
      return null
    }
  }

  // 地図表示処理
  async onClickMap () {
    const centerAddress = this.getCurrentCenterAddress()
    if (!centerAddress) {
      this.showError('中心地の住所が設定されていません。')
      return
    }

    // 地図が初期化されていない場合は初期化を試行
    if (!this._map) {
      const mapResult = this.initializeMap()
      if (!mapResult) {
        this.showError('地図の初期化に失敗しました。')
        return
      }
    }

    const geocoder = new google.maps.Geocoder()
    const results = await new Promise((resolve, reject) => {
      geocoder.geocode({ address: centerAddress }, (results, status) => {
        if (results && results.length > 0) {
          resolve(results)
        } else {
          this.showError('住所が見つかりませんでした。もう一度入力してください。')
          reject(new Error('住所が見つかりませんでした。'))
        }
      })
    })

    const latLng = results[0].geometry.location
    this.location = { lat: latLng.lat(), lon: latLng.lng() }
    const params = this.collectConditionMatchingParams()

    // 移動時間から距離を算出して、その範囲内の市区町村を自動選択
    const searchDistance = this.getSearchDistanceFromTravelTime()
    await this.fetchCompanyCounts(params)
    this.autoSelectCitiesWithinDistance(searchDistance)

    this.loadGeoJSON()
    if (this._map) {
      this.map.setView([latLng.lat(), latLng.lng()], 11)
    }
  }

  // condition_matchingのattributeに対応するパラメータを全て取得
  collectConditionMatchingParams () {
    // 安全にターゲット値を取得するヘルパー関数
    const safeGetValue = (target) => {
      try {
        return target ? target.value : null
      } catch {
        return null
      }
    }

    const safeGetChecked = (target) => {
      try {
        return target ? target.checked : false
      } catch {
        return false
      }
    }

    // 入居一時金・月額料金（単位: 万円 → 円に変換）
    const admissionFeeMin = safeGetValue(this.hasAdmissionFeeMinTarget ? this.admissionFeeMinTarget : null)
    const admissionFeeMax = safeGetValue(this.hasAdmissionFeeMaxTarget ? this.admissionFeeMaxTarget : null)
    const monthlyFeeMin = safeGetValue(this.hasMonthlyFeeMinTarget ? this.monthlyFeeMinTarget : null)
    const monthlyFeeMax = safeGetValue(this.hasMonthlyFeeMaxTarget ? this.monthlyFeeMaxTarget : null)
    // 施設検索用月額料金（自動計算済み）
    const modifiedMonthlyFeeMin = safeGetValue(this.hasModifiedMonthlyFeeMinTarget ? this.modifiedMonthlyFeeMinTarget : null)
    const modifiedMonthlyFeeMax = safeGetValue(this.hasModifiedMonthlyFeeMaxTarget ? this.modifiedMonthlyFeeMaxTarget : null)
    // 移動時間
    const travelTimeMinutes = safeGetValue(this.hasTravelTimeMinutesTarget ? this.travelTimeMinutesTarget : null)
    // 住民票住所
    const residentPrefectureId = safeGetValue(this.hasResidentPrefectureIdTarget ? this.residentPrefectureIdTarget : null)
    const residentCityId = safeGetValue(this.hasResidentCityIdTarget ? this.residentCityIdTarget : null)

    const currentCareLevelId = safeGetValue(this.hasCurrentCareLevelIdTarget ? this.currentCareLevelIdTarget : null)
    const estimatedCareLevelId = safeGetValue(this.hasEstimatedCareLevelIdTarget ? this.estimatedCareLevelIdTarget : null)

    // 介護度（見込み介護度優先）
    let careLevelId = estimatedCareLevelId
    if (!careLevelId || careLevelId === '' || careLevelId === 'same_as_current') {
      careLevelId = currentCareLevelId
    }

    // 介護保険自己負担割合
    const careInsuranceRateId = safeGetValue(this.hasCareInsuranceRateIdTarget ? this.careInsuranceRateIdTarget : null)

    // チェックボックス
    const foodPlanExist = safeGetChecked(this.hasFoodPlanExistTarget ? this.foodPlanExistTarget : null)
    const welfare = safeGetChecked(this.hasWelfareTarget ? this.welfareTarget : null)
    const includeFullOccupancy = safeGetChecked(this.hasIncludeFullOccupancyTarget ? this.includeFullOccupancyTarget : null)
    const includeLowMatchingScore = safeGetChecked(this.hasIncludeLowMatchingScoreTarget ? this.includeLowMatchingScoreTarget : null)
    const includeHospice = safeGetChecked(this.hasIncludeHospiceTarget ? this.includeHospiceTarget : null)
    const doubleRoom = safeGetChecked(this.hasDoubleRoomTarget ? this.doubleRoomTarget : null)
    const removeNursingCare = safeGetChecked(this.hasRemoveNursingCareTarget ? this.removeNursingCareTarget : null)

    // 介護度（複数）- 旧ロジック（必要に応じて維持）
    const careLevelIds = []
    try {
      if (this.targets) {
        const careLevelTargets = this.targets.findAll('careLevel')
        careLevelTargets.filter(e => e.checked).forEach(e => {
          if (e.value) careLevelIds.push(e.value)
        })
      }
    } catch {
      // エラーが発生した場合は空配列
    }

    // 施設グループ（推奨グループ選定で選ばれたもの）
    const facilityGroupIds = this.facilityGroups ? this.facilityGroups.map(group => group.id).filter(id => id) : []

    // エリア検索の都道府県・市区町村
    const areaSearchPrefectureId = safeGetValue(this.hasAreaSearchPrefectureIdTarget ? this.areaSearchPrefectureIdTarget : null)
    const areaSearchCityId = safeGetValue(this.hasAreaSearchCityIdTarget ? this.areaSearchCityIdTarget : null)

    return {
      location: this.location || { lat: 0, lon: 0 },
      admission_fee_min: admissionFeeMin,
      admission_fee_max: admissionFeeMax,
      monthly_fee_min: monthlyFeeMin,
      monthly_fee_max: monthlyFeeMax,
      modified_monthly_fee_min: modifiedMonthlyFeeMin,
      modified_monthly_fee_max: modifiedMonthlyFeeMax,
      travel_time_minutes: travelTimeMinutes,
      resident_prefecture_id: residentPrefectureId,
      resident_city_id: residentCityId,
      current_care_level_id: currentCareLevelId,
      estimated_care_level_id: estimatedCareLevelId,
      care_level_id: careLevelId,
      care_insurance_rate_id: careInsuranceRateId,
      food_plan_exist: foodPlanExist,
      welfare,
      include_full_occupancy: includeFullOccupancy,
      include_low_matching_score: includeLowMatchingScore,
      include_hospice: includeHospice,
      care_level_ids: careLevelIds,
      facility_group_ids: facilityGroupIds,
      remove_nursing_care: removeNursingCare,
      double_room: doubleRoom,
      area_search_prefecture_id: areaSearchPrefectureId,
      area_search_city_id: areaSearchCityId,
    }
  }

  // パラメータをURLSearchParamsに詰めてAPIリクエスト
  async fetchCompanyCounts (paramsObj) {
    const params = this.buildURLSearchParams(paramsObj)
    try {
      const res = await fetch(`/api/admin/ca/requests/${this.requestIdTarget.value}/condition_matching?${params.toString()}`, {
        method: 'GET',
        headers: this.header,
      })
      if (!res.ok) await this.handleApiError(res, 'APIエラー')
      const data = await res.json()
      // 距離ごとに分けてcitiesByRangeへ格納
      this.citiesByRange = {
        within_5km: data.cities.within_5km || [],
        within_10km: data.cities.within_10km || [],
        within_15km: data.cities.within_15km || [],
        within_20km: data.cities.within_20km || [],
        within_25km: data.cities.within_25km || [],
        within_30km: data.cities.within_30km || [],
        within_35km: data.cities.within_35km || [],
        within_40km: data.cities.within_40km || [],
      }
      this.companyCountByCity = data.company_count_by_city
      this.renderAllCityLists()
      this.syncSelectedCities()
    } catch (error) {
      this.showError(`施設情報の取得に失敗しました: ${error.message}`)
    }
  }

  // 各距離ごとに市区町村リストをdetailsタグで描画
  renderAllCityLists () {
    if (!this.hasNearByCitiesTarget) {
      return
    }

    let html = ''
    const ranges = [5, 10, 15, 20, 25, 30, 35, 40]
    // Leafletのcircleを距離ごとに描画
    if (this._map && this.location.lat && this.location.lon) {
      // 既存circleを削除（再描画時）
      if (this._distanceCircles) {
        this._distanceCircles.forEach(obj => {
          this.map.removeLayer(obj.circle)
          this.map.removeLayer(obj.label)
        })
      }
      this._distanceCircles = []
      ranges.forEach(km => {
        const circle = L.circle([this.location.lat, this.location.lon], {
          radius: km * 1000, // km→m
          color: '#8E24AA', // 紫系
          weight: 2,
          fill: false,
        })
        circle.addTo(this.map)
        // 円の右下にラベル（距離表示）
        const labelLat = this.location.lat + (km / 111) * 0.7 // 右下方向にずらす（緯度1度≒111km）
        const labelLon = this.location.lon + (km / (111 * Math.cos(this.location.lat * Math.PI / 180))) * 0.7
        const label = L.marker([labelLat, labelLon], {
          icon: L.divIcon({
            className: 'distance-label',
            html: `<span style='background:rgba(230,210,240,0.8);border:1px solid #8E24AA;border-radius:4px;padding:2px 6px;font-size:12px;'>${km}km</span>`,
          }),
          interactive: false,
        })
        label.addTo(this.map)
        this._distanceCircles.push({ circle, label })
      })
    }
    ranges.forEach((km, idx) => {
      const key = `within_${km}km`
      const cities = this.citiesByRange[key] || []
      html += `
        <details ${idx === 0 ? 'open' : ''} style="margin-bottom:8px;">
          <summary style="font-weight:bold;cursor:pointer;">${km}km以内</summary>
          <div style="margin-top:8px;">
            ${this.renderCityList(`${km}km以内`, cities, key)}
          </div>
        </details>
      `
    })
    this.nearByCitiesTarget.innerHTML = html
    this.updateAllCheckboxStates()
    // detailsタグのアコーディオン挙動: 1つだけ開く
    const detailsList = this.nearByCitiesTarget.querySelectorAll('details')
    detailsList.forEach(details => {
      details.addEventListener('toggle', function () {
        if (details.open) {
          detailsList.forEach(other => {
            if (other !== details) other.open = false
          })
        }
      })
    })

    // ページリロード時にselectedCityCodesValueの市区町村コードにチェックを入れる
    if (Array.isArray(this.cityCodesValue)) {
      this.cityCodesValue.forEach(jiscode => {
        const checkbox = this.nearByCitiesTarget.querySelector(`.city-checkbox[data-jis-code="${jiscode}"]`)
        if (checkbox) {
          checkbox.checked = true
        }
      })
    }
  }

  // 市区町村リストHTML生成
  renderCityList (label, cities, rangeKey) {
    if (!cities.length) return ''
    return `
      <div class="border rounded p-2">
        <input id="${rangeKey}" type="checkbox" class="select-all-checkbox" data-range="${rangeKey}">
        <b><label for="${rangeKey}" style="margin-left:4px;">${label}</label></b>
        <div class="${this.viewComponentClassName}::city_within_range" style="height:100px;overflow-y:auto;"><ul>
      ` + cities.map(c => `
      <li>
        <input
          type="checkbox"
          id="${c.jis_code}"
          class="city-checkbox"
          data-jis-code="${c.jis_code}"
          data-city-name="${c.name}"
          data-company-count="${c.company_count}"
          data-range="${rangeKey}" ${this.selectedCities.some(city => city.jiscode === c.jis_code) ? 'checked' : ''}
        />
        <label for="${c.jis_code}">${c.name} ${c.company_count > 0 ? `(法人${c.company_count}件)` : ''}</label>
      </li>`).join('') + '</ul></div></div>'
  }

  // チェックボックスの状態を一括で更新
  updateAllCheckboxStates () {
    [
      'within_5km',
      'within_10km',
      'within_15km',
      'within_20km',
      'within_25km',
      'within_30km',
      'within_35km',
      'within_40km',
    ].forEach(range => {
      this.updateSelectAllCheckbox(range)
    })
  }

  // マップ右下の凡例を追加
  addLegend (map) {
    const grades = ['範囲外', 0, 1, 3, 6]
    const legend = L.control({ position: 'bottomright' })
    legend.onAdd = (_) => {
      const div = L.DomUtil.create('div', `${this.viewComponentClassName}::legend`)
      const labels = []
      for (let i = 0; i < grades.length; i++) {
        const from = grades[i]
        const to = grades[i + 1]
        const color = from === '範囲外' ? '#ffffff' : this.getStyle(from)
        const range = ['範囲外', 0].includes(from) ? from : to ? `${from}-${to}` : `${from}+`
        labels.push(
          `<div style="display:flex;align-items:center;"><i style="background:${color};"></i>${range}</div>`,
        )
      }
      div.innerHTML = labels.join('')
      return div
    }
    legend.addTo(map)
  }

  // マップの市区町村の色の設定
  defaultStyle (feature) {
    const code = feature.properties.N03_007
    const found = Object.values(this.citiesByRange).some(list => list.find(c => c.jis_code === code))
    if (found) {
      const companyCount = this.companyCountByCity[code] || 0
      return {
        weight: 1,
        color: this.getStyle(companyCount),
        dashArray: '3',
        fillOpacity: 0.3,
      }
    } else {
      return {
        weight: 2,
        color: '#ffffff',
        dashArray: '3',
        fillOpacity: 0.5,
      }
    }
  }

  // 法人数に応じた色を返す
  getStyle (count) {
    return count > 5
      ? '#EA4335'
      : count > 2
        ? '#34A853'
        : count > 0
          ? '#4285F4'
          : '#9E9E9E'
  }

  loadGeoJSON () {
    if (this._geojson) {
      this.map.removeLayer(this._geojson)
      this._geojson = null
    }
    this.geojson.addTo(this.map)
  }

  onEachFeature (feature, layer) {
    layer.on({
      mouseover: (e) => this.highlightFeature(e),
      mouseout: (e) => this.resetHighlight(e),
      click: (e) => this.addSelectedCity(e),
    })
    const cityName = `${feature.properties.N03_003 || ''}${feature.properties.N03_004 || ''}${feature.properties.N03_005 || ''}`
    const companyCount = this.companyCountByCity[feature.properties.N03_007] || 0
    layer.bindTooltip(`${cityName}（法人数: ${companyCount}）`, { sticky: true })
  }

  highlightFeature = (e) => {
    const layer = e.target
    layer.setStyle({
      weight: 5,
      fillOpacity: 0.7,
    })
    layer.bringToFront()
  }

  resetHighlight = (e) => {
    if (this.geojson && this.geojson.resetStyle) {
      this.geojson.resetStyle(e.target)
    }
  }

  // 選択中の市区町村への追加
  addCity (jiscode, name, companyCount) {
    if (!this.selectedCities.some(c => c.jiscode === jiscode)) {
      this.selectedCities.push({ jiscode, name, companyCount })
    }
  }

  // 選択中の市区町村からの削除
  removeCity (jiscode) {
    this.selectedCities = this.selectedCities.filter(c => c.jiscode !== jiscode)
  }

  // 地図上の市区町村を選択したときの処理
  addSelectedCity = (e) => {
    const layer = e.target
    const jiscode = layer.feature.properties.N03_007
    const name = `${layer.feature.properties.N03_003 || ''}${layer.feature.properties.N03_004 || ''}${layer.feature.properties.N03_005 || ''}`
    const companyCount = this.companyCountByCity[jiscode] || 0
    const checkbox = this.nearByCitiesTarget.querySelector(`.city-checkbox[data-jis-code="${jiscode}"]`)
    if (this.selectedCities.some(c => c.jiscode === jiscode)) {
      this.removeCity(jiscode)
      if (checkbox) checkbox.checked = false
    } else {
      this.addCity(jiscode, name, companyCount)
      if (checkbox) checkbox.checked = true
    }
    this.updateSelectedCities()
    // 個別チェックボックスの状態で全選択チェックボックスも更新
    if (checkbox) this.updateSelectAllCheckbox(checkbox.getAttribute('data-range'))
  }

  // イベントデリゲーションでチェックボックスの挙動を管理
  handleCheckboxChange = (e) => {
    const target = e.target
    if (target.classList.contains('city-checkbox')) {
      // 個別の市区町村チェックボックスの変更
      const jiscode = target.getAttribute('data-jis-code')
      const name = target.getAttribute('data-city-name')
      const companyCount = Number(target.getAttribute('data-company-count')) || 0
      if (target.checked) {
        this.addCity(jiscode, name, companyCount)
      } else {
        this.removeCity(jiscode)
      }
      this.updateSelectAllCheckbox(target.getAttribute('data-range'))
    } else if (target.classList.contains('select-all-checkbox')) {
      // 全選択チェックボックスの変更
      const range = target.getAttribute('data-range')
      const checkboxes = this.nearByCitiesTarget.querySelectorAll(`.city-checkbox[data-range="${range}"]`)
      checkboxes.forEach(cb => {
        cb.checked = target.checked
        const jiscode = cb.getAttribute('data-jis-code')
        const name = cb.getAttribute('data-city-name')
        const companyCount = Number(cb.getAttribute('data-company-count')) || 0

        if (target.checked) {
          this.addCity(jiscode, name, companyCount)
        } else {
          this.removeCity(jiscode)
        }
      })
    }

    this.updateSelectedCities()
  }

  // 全選択チェックボックスの状態を更新
  updateSelectAllCheckbox (range) {
    const checkboxes = this.nearByCitiesTarget.querySelectorAll(`.city-checkbox[data-range="${range}"]`)
    const selectAll = this.nearByCitiesTarget.querySelector(`.select-all-checkbox[data-range="${range}"]`)
    if (!selectAll) return
    const allChecked = Array.from(checkboxes).every(cb => cb.checked)
    selectAll.checked = allChecked
  }

  // 選択中の市区町村をnearByCitiesTargetから取得してselectedCitiesに同期
  syncSelectedCities () {
    if (!this.hasNearByCitiesTarget) {
      this.updateSelectedCities()
      return
    }

    this.selectedCities = Array.from(this.nearByCitiesTarget.querySelectorAll('.city-checkbox:checked')).map(cb => {
      return {
        jiscode: cb.getAttribute('data-jis-code'),
        name: cb.getAttribute('data-city-name'),
        companyCount: Number(cb.getAttribute('data-company-count')) || 0,
      }
    })
    this.updateSelectedCities()
  }

  // 選択中の市区町村表示を更新
  updateSelectedCities () {
    // selectedCitiesTargetが存在しない場合は何もしない
    if (!this.hasSelectedCitiesTarget) {
      return
    }

    if (this.selectedCities.length) {
      this.renderSelectedCities()
    } else {
      this.selectedCitiesTarget.innerHTML = '<span class="text-muted">市区町村が選択されていません</span>'
      if (this.hasSearchButtonTarget) {
        this.searchButtonTarget.disabled = true
      }
    }
  }

  renderSelectedCities () {
    if (!this.hasSelectedCitiesTarget) {
      return
    }

    const content = `
      <table class='table'>
        <thead>
          <tr>
            <th>市区町村名</th>
            <th>法人数</th>
          </tr>
        </thead>
        <tbody>
          ${this.selectedCities.map(city => `
            <tr>
              <td>${city.name}</td>
              <td>${city.companyCount}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `
    this.selectedCitiesTarget.innerHTML = content
    if (this.hasSearchButtonTarget) {
      this.searchButtonTarget.disabled = false
    }
  }

  /**
   * 必須項目が揃っているかチェック
   */
  isSearchConditionReady () {
    return this.hasValidationResultValue && this.validationResultValue.search_ready
  }

  /**
   * 条件妥当性レビュー、施設一覧
   */
  async onClickSearch () {
    // 検索ボタンが無効化されている場合は処理しない
    if (this.hasSearchButtonTarget && this.searchButtonTarget.disabled) {
      console.log('Search button is disabled, skipping search')
      return
    }

    const params = this.collectConditionMatchingParams()
    params.physical_condition = this.physicalCondition || {}
    params.center_area = this.getCurrentCenterAddress() || ''
    params.city_codes = this.selectedCities ? this.selectedCities.map(city => city.jiscode).filter(code => code) : []
    params.area_detail_address = this.hasAreaDetailAddressTarget ? this.areaDetailAddressTarget.value : ''

    // デバッグ用：パラメータをコンソールに出力
    console.log('API Request Parameters:', params)

    try {
      const url = `/api/admin/ca/requests/${this.requestIdTarget.value}/condition_matching`
      const res = await fetch(url, {
        method: 'POST',
        headers: this.header,
        body: JSON.stringify(params),
      })

      if (!res.ok) {
        // エラーレスポンスの詳細をログ出力
        let errorText
        try {
          errorText = await res.text()
          console.error('API Error Response:', errorText)
          console.error('Status:', res.status, 'StatusText:', res.statusText)
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError)
          errorText = `HTTP ${res.status}: ${res.statusText}`
        }
        throw new Error(errorText || 'API request failed')
      }

      const data = await res.json()
      this.updateConditionReview(data)
      this.updateConditionNavigation(data)
      this.renderFacilityGroups(data.facility_groups)
    } catch (error) {
      console.error('Search Error:', error)
      this.showError(`施設検索に失敗しました: ${error.message}`)
    } finally {
      if (this.hasMainContainerTarget) {
        this.mainContainerTarget.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }

  // 条件妥当性レビューの更新
  updateConditionReview (data) {
    // APIレスポンスからvalidity_reviewオブジェクトを取得
    const validityReview = data.validity_review || {}

    // 1. 法人数の充足度（review1_passed: 法人5件以上）
    const isReview1Passed = validityReview.review1_passed === true
    if (this.hasCompanyCountReviewTarget) {
      this.companyCountReviewTarget.innerHTML = isReview1Passed
        ? `<i class="far fa-circle ${this.viewComponentClassName}::fa-circle"></i>`
        : `<i class="fas fa-times ${this.viewComponentClassName}::fa-times"></i>`
    }

    // 2. グループごとの網羅性（review2_passed: 全てのグループで法人1件以上）
    const isReview2Passed = validityReview.review2_passed === true
    if (this.hasGroupCoverageReviewTarget) {
      this.groupCoverageReviewTarget.innerHTML = isReview2Passed
        ? `<i class="far fa-circle ${this.viewComponentClassName}::fa-circle"></i>`
        : `<i class="fas fa-times ${this.viewComponentClassName}::fa-times"></i>`
    }
  }

  // 条件ナビの更新
  updateConditionNavigation (data) {
    // APIレスポンスからrecommendationsオブジェクトを取得
    const recommendations = data.recommendations || {}
    const validityReview = data.validity_review || {}

    // 条件妥当性レビューが両方とも通っている場合は条件ナビを非表示
    const isReview1Passed = validityReview.review1_passed === true
    const isReview2Passed = validityReview.review2_passed === true

    if (isReview1Passed && isReview2Passed) {
      // 両方のレビューが通っている場合は条件ナビを非表示
      if (this.hasConditionNavigationTarget) {
        this.conditionNavigationTarget.style.display = 'none'
      }
      return
    }

    // どちらかのレビューが通っていない場合は条件ナビを表示
    if (this.hasConditionNavigationTarget) {
      this.conditionNavigationTarget.style.display = 'block'
    }

    // 推奨エリア（推奨半径）の表示
    if (this.hasRecommendedRadiusTarget) {
      const recommendedRadius = recommendations.recommended_radius
      if (recommendedRadius && recommendedRadius.distance) {
        this.recommendedRadiusTarget.innerHTML = `
          <div class="alert alert-info mb-0">
            <strong>${recommendedRadius.distance}km まで広げると、${recommendedRadius.increased_count}件 増えます</strong>
          </div>
        `
      } else {
        this.recommendedRadiusTarget.innerHTML = `
          <p class="text-muted mb-0">40kmまで広げましたが、十分な施設が確保できません。予算を上げることを検討してください</p>
        `
      }
    }

    // 推奨月額予算の表示
    if (this.hasRecommendedBudgetTarget) {
      const recommendedBudget = recommendations.recommended_monthly_budget
      if (recommendedBudget && recommendedBudget.user_budget) {
        this.recommendedBudgetTarget.innerHTML = `
          <div class="alert alert-info mb-0">
            <strong>${recommendedBudget.user_budget}万円（+${recommendedBudget.increase_amount}万円） まで予算を上げると、${recommendedBudget.increased_count}件 増えます</strong>
          </div>
        `
      } else {
        const currentMonthlyFeeMax = this.hasMonthlyFeeMaxTarget ? (Number(this.monthlyFeeMaxTarget.value) || 0) : 0
        const maxBudget = currentMonthlyFeeMax + 20
        this.recommendedBudgetTarget.innerHTML = `
          <p class="text-muted mb-0">${maxBudget}万円まで上げましたが、十分な施設が確保できません。エリアを広げることを検討してください</p>
        `
      }
    }
  }

  renderFacilityGroups (data) {
    this.lastFacilityData = data
    this.facilityListTarget.innerHTML = `
      <div class="${this.viewComponentClassName}::facility-group-list">
        ${Object.entries(data).map(([groupId, groupData]) => {
          const facilityGroup = this.facilityGroups.find(group => group.id === Number(groupId))
          return this.renderFacilityGroup(facilityGroup, groupData)
        }).join('')}
      </div>
    `
  }

  renderFacilityGroup (facilityGroup, groupData) {
    return `
      <div class="${this.viewComponentClassName}::facility-group-item">
        <div class="${this.viewComponentClassName}::facility-group-name">${facilityGroup.name}</div>
        ${groupData.facilities.length > 0
          ? `
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th>施設情報</th>
                    <th>成約金額（新規）</th>
                    <th>特別単価（新規）</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  ${groupData.facilities.map(facility => this.renderFacilityItem(facility, facility.ca_request_facility_id, facilityGroup.id)).join('')}
                </tbody>
              </table>
            `
          : `<div class="${this.viewComponentClassName}::facility-item text-muted">該当施設なし</div>`}
      </div>
    `
  }

  renderFacilityItem (facility, caRequestFacilityId = null, facilityGroupId) {
    return `
      <tr id="facility-item-${facility.id}">
        <td>
          <div class="${this.viewComponentClassName}::facility-name">${facility.name}</div>
          <div class="${this.viewComponentClassName}::facility-info text-muted">
            ${facility.company_name} / ${facility.city_name} / ${facility.category_name}
          </div>
        </td>
        <td>${facility.unit_price}</td>
        <td>
          ${facility.has_active_special_unit_price
            ? (facility.special_unit_price_pdf_url
                ? `<a href="${facility.special_unit_price_pdf_url}" target="_blank" rel="noopener noreferrer"><span class="badge badge-primary">特単</span></a>`
                : '<span class="badge badge-primary">特単</span>')
            : ''}
        </td>
        <td>
          <button type="button"
            class="${this.viewComponentClassName}__candidate-btn btn ${caRequestFacilityId ? 'btn-danger' : 'btn-warning'}"
            data-action="click->${this.identifier}#toggleTourCandidate"
            data-ca-request-facility-id="${caRequestFacilityId || ''}"
            data-facility-group-id="${facilityGroupId}"
            data-facility-id="${facility.id}">
            ${caRequestFacilityId ? '候補から外す' : '候補にする'}
          </button>
        </td>
      </tr>
    `
  }

  // 施設候補のトグル処理
  async toggleTourCandidate (event) {
    const facilityId = Number(event.currentTarget.getAttribute('data-facility-id'))
    let caRequestFacilityId = event.currentTarget.getAttribute('data-ca-request-facility-id')
    const facilityGroupId = event.currentTarget.getAttribute('data-facility-group-id')

    if (caRequestFacilityId) {
      // 削除API呼び出し
      await this.deleteTourCandidate(caRequestFacilityId, facilityId)
      caRequestFacilityId = null // 削除後はIDをnullに設定
    } else {
      // 追加API呼び出し
      const newCaRequestFacilityId = await this.addTourCandidate(facilityId, facilityGroupId)
      caRequestFacilityId = newCaRequestFacilityId
    }
    // 部分描画: 対象施設のみ差し替え
    const wrapper = document.getElementById(`facility-item-${facilityId}`)
    if (wrapper && this.lastFacilityData) {
      // groupDataを特定
      let facilityObj = null
      Object.values(this.lastFacilityData).forEach(groupData => {
        const found = groupData.facilities.find(f => f.id === facilityId)
        if (found) facilityObj = found
      })
      if (facilityObj) {
        wrapper.outerHTML = this.renderFacilityItem(facilityObj, caRequestFacilityId)
      }
    }
  }

  // 追加API
  async addTourCandidate (facilityId, facilityGroupId) {
    const url = '/api/admin/ca/request/facilities'

    return await fetch(url, {
      method: 'POST',
      headers: this.header,
      body: JSON.stringify({ request_id: this.requestIdTarget.value, facility_id: facilityId, facility_group_id: facilityGroupId }),
    })
      .then(response => {
        if (response.ok) return response.json()
        throw new Error(response.statusText)
      })
      .then(data => {
        return data.id
      })
      .catch(_ => {
        this.showError('施設候補を追加できませんでした。')
      })
  }

  // 削除API
  async deleteTourCandidate (caRequestFacilityId, facilityId) {
    const url = `/api/admin/ca/request/facilities/${caRequestFacilityId}?request_id=${this.requestIdTarget.value}&facility_id=${facilityId}`

    await fetch(url, {
      method: 'DELETE',
      headers: this.header,
    })
      .catch(_ => {
        this.showError('施設候補を削除できませんでした。')
      })
  }

  /**
   * 共通処理
   */
  // 共通のAPIエラーハンドリング関数
  async handleApiError (response, defaultMessage = 'APIエラー') {
    let message = defaultMessage
    try {
      const errorData = await response.json()
      if (errorData && errorData.errors && errorData.errors[0]) {
        message = errorData.errors[0]
      } else if (response.statusText) {
        message = response.statusText
      }
    } catch (_) {
      if (response.statusText) message = response.statusText
    }
    throw new Error(message)
  }

  // エラー表示用メソッド
  showError (message) {
    // Bootstrapのアラートコンポーネントなどを使用
    const errorDiv = document.createElement('div')
    errorDiv.className = 'alert alert-danger alert-dismissible fade show mt-3'
    errorDiv.innerHTML = `
      <span>${message}</span>
      <button type="button" class="close" data-bs-dismiss="alert" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    `
    // 閉じるボタンでアラートを消す
    errorDiv.querySelector('.close').addEventListener('click', () => {
      errorDiv.remove()
    })
    this.element.prepend(errorDiv)
  }

  buildURLSearchParams (paramsObj) {
    const params = new URLSearchParams()
    Object.entries(paramsObj).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => params.append(`${key}[]`, v))
      } else if (typeof value === 'object' && value !== null) {
        Object.entries(value).forEach(([k, v]) => {
          params.append(`${key}[${k}]`, v)
        })
      } else if (typeof value === 'boolean') {
        params.append(key, value ? 'true' : 'false')
      } else if (value !== undefined && value !== null && value !== '') {
        params.append(key, value)
      }
    })
    return params
  }

  // STEP2: 施設検索用月額料金の自動計算
  async calculateModifiedMonthlyFee () {
    const monthlyFeeMin = Number(this.monthlyFeeMinTarget.value) || 0
    const monthlyFeeMax = Number(this.monthlyFeeMaxTarget.value) || 0

    const careLevelId = this.getCurrentCareLevel()
    const careInsuranceRate = Number(this.careInsuranceRateIdTarget.value) || 0

    if (!careLevelId || !careInsuranceRate) {
      // 介護度または自己負担割合が未設定の場合、元の値をそのまま設定
      this.updateModifiedFeeFields(monthlyFeeMin || '', monthlyFeeMax || '')
      return
    }

    // API呼び出しを試み、失敗した場合はローカル計算にフォールバック
    try {
      await this.calculateModifiedMonthlyFeeViaAPI()
    } catch (error) {
      console.warn('API calculation failed, falling back to local calculation:', error)
      const result = this.calculateModifiedMonthlyFeeLocal(monthlyFeeMin, monthlyFeeMax, careLevelId, careInsuranceRate)
      this.updateModifiedFeeFields(
        result.min === 0 ? '' : result.min,
        result.max === 0 ? '' : result.max,
      )
    }
  }

  // ローカルでの月額料金計算
  calculateModifiedMonthlyFeeLocal (monthlyFeeMin, monthlyFeeMax, careLevelId, careInsuranceRate) {
    const careInsuranceBurden = this.calculateCareInsuranceBurden(careLevelId, careInsuranceRate)

    return {
      min: Math.max(0, monthlyFeeMin - careInsuranceBurden),
      max: Math.max(0, monthlyFeeMax - careInsuranceBurden),
    }
  }

  async calculateModifiedMonthlyFeeViaAPI () {
    const monthlyFeeMin = Number(this.monthlyFeeMinTarget.value) || 0
    const monthlyFeeMax = Number(this.monthlyFeeMaxTarget.value) || 0
    const careLevelId = this.getCurrentCareLevel()
    const careInsuranceRate = Number(this.careInsuranceRateIdTarget.value) || 0

    if (!careLevelId || !careInsuranceRate) {
      this.updateModifiedFeeFields(monthlyFeeMin || '', monthlyFeeMax || '')
      return
    }

    const url = `/api/admin/ca/requests/${this.requestIdTarget.value}/monthly_fee_calculations`
    const res = await fetch(url, {
      method: 'POST',
      headers: this.header,
      body: JSON.stringify({
        monthly_fee_min: monthlyFeeMin,
        monthly_fee_max: monthlyFeeMax,
        care_level_id: careLevelId,
        care_insurance_rate: careInsuranceRate,
      }),
    })
    if (!res.ok) await this.handleApiError(res, 'APIエラー')
    const data = await res.json()

    // APIから定数も取得可能
    if (data.care_level_constants) {
      this.careLevelConstants = data.care_level_constants
    }

    this.updateModifiedFeeFields(
      data.modified_monthly_fee_min === 0 ? '' : data.modified_monthly_fee_min,
      data.modified_monthly_fee_max === 0 ? '' : data.modified_monthly_fee_max,
    )
  }

  // 現在の介護度を取得（見込み介護度優先）
  getCurrentCareLevel () {
    const estimatedCareLevel = this.estimatedCareLevelIdTarget.value
    const currentCareLevel = this.currentCareLevelIdTarget.value

    // 見込み介護度が「現状と同じ」の場合は現在の介護度を参照
    if (estimatedCareLevel === 'same_as_current') {
      return currentCareLevel
    }
    return estimatedCareLevel && estimatedCareLevel !== '' ? estimatedCareLevel : currentCareLevel
  }

  // 介護保険自己負担額の計算（ローカルフォールバック用）
  calculateCareInsuranceBurden (careLevelId, careInsuranceRate) {
    const careLevelNum = Number(careLevelId)

    // 介護度定数（APIから取得した値を使用、なければデフォルト値）
    const CARE_LEVELS = this.careLevelConstants || {
      SUPPORT_1: 11,
      SUPPORT_2: 12,
      NURSING_1: 21,
      NURSING_2: 22,
      NURSING_3: 23,
      NURSING_4: 24,
      NURSING_5: 25,
    }

    const burdenMap = {
      light: { 1: 1, 2: 2, 3: 2 }, // 要支援1〜要介護1
      medium: { 1: 3, 2: 5, 3: 7 }, // 要介護2〜3
      heavy: { 1: 5, 2: 8, 3: 10 }, // 要介護4〜5
    }

    let category
    if ([CARE_LEVELS.SUPPORT_1, CARE_LEVELS.SUPPORT_2, CARE_LEVELS.NURSING_1].includes(careLevelNum)) {
      category = 'light'
    } else if ([CARE_LEVELS.NURSING_2, CARE_LEVELS.NURSING_3].includes(careLevelNum)) {
      category = 'medium'
    } else if ([CARE_LEVELS.NURSING_4, CARE_LEVELS.NURSING_5].includes(careLevelNum) || careLevelNum > 25) {
      category = 'heavy'
    } else {
      return 0 // 不明な介護度
    }

    return burdenMap[category][careInsuranceRate] || 0
  }

  // 施設検索用月額料金フィールドの更新
  updateModifiedFeeFields (minValue, maxValue) {
    this.modifiedMonthlyFeeMinTarget.value = minValue
    this.modifiedMonthlyFeeMaxTarget.value = maxValue
  }

  // 移動時間から検索距離を取得
  getSearchDistanceFromTravelTime () {
    const travelTime = parseInt(this.travelTimeMinutesTarget.value)
    if (!travelTime) return null

    // 移動時間→距離変換
    // 10分→5km、20分→10km、30分→10km、40分→15km、50分→15km、60分→20km
    if (travelTime <= 10) return 5
    if (travelTime <= 30) return 10
    if (travelTime <= 50) return 15
    if (travelTime <= 60) return 20
    return 20 // 60分以上は20km
  }

  // 距離内の市区町村を自動選択
  autoSelectCitiesWithinDistance (maxDistance) {
    // 既存の選択をクリア
    this.selectedCities = []

    // maxDistanceがnullの場合は何も選択しない
    if (maxDistance === null) {
      this.updateCityCheckboxes()
      this.updateSelectedCities()
      return
    }

    // 指定距離以内の全市区町村を選択
    const ranges = [
      { key: 'within_5km', distance: 5 },
      { key: 'within_10km', distance: 10 },
      { key: 'within_15km', distance: 15 },
      { key: 'within_20km', distance: 20 },
      { key: 'within_25km', distance: 25 },
      { key: 'within_30km', distance: 30 },
      { key: 'within_35km', distance: 35 },
      { key: 'within_40km', distance: 40 },
    ]

    ranges.forEach(range => {
      if (range.distance <= maxDistance) {
        const cities = this.citiesByRange[range.key] || []
        cities.forEach(city => {
          if (!this.selectedCities.some(c => c.jiscode === city.jis_code)) {
            this.selectedCities.push({
              jiscode: city.jis_code,
              name: city.name,
              companyCount: city.company_count,
            })
          }
        })
      }
    })

    // チェックボックスの状態を更新
    this.updateCityCheckboxes()
    this.updateSelectedCities()
  }

  // チェックボックスの選択状態を更新
  updateCityCheckboxes () {
    // nearByCitiesTargetが存在しない場合は何もしない
    if (!this.hasNearByCitiesTarget) {
      return
    }

    // 全てのチェックボックスをクリア
    const allCheckboxes = this.nearByCitiesTarget.querySelectorAll('.city-checkbox')
    allCheckboxes.forEach(cb => {
      cb.checked = false
    })

    // 選択された市区町村のチェックボックスをチェック
    this.selectedCities.forEach(city => {
      const checkbox = this.nearByCitiesTarget.querySelector(`.city-checkbox[data-jis-code="${city.jiscode}"]`)
      if (checkbox) {
        checkbox.checked = true
      }
    })

    // 全選択チェックボックスの状態も更新
    this.updateAllCheckboxStates()
  }

  // 共通の都道府県変更処理
  async handlePrefectureChange (prefectureId, citySelect, timeoutProperty, afterCityUpdate = null) {
    // debounce処理をキャンセル
    if (this[timeoutProperty]) {
      clearTimeout(this[timeoutProperty])
    }

    this.resetCityOptions(citySelect)

    if (afterCityUpdate) {
      afterCityUpdate()
    }

    if (!prefectureId) {
      return
    }

    // ローディング状態を表示
    this.showCityLoading(citySelect)

    // debounce処理（300ms後に実行）
    this[timeoutProperty] = setTimeout(async () => {
      try {
        const cities = await this.fetchCitiesForPrefecture(prefectureId)
        this.populateCityOptions(citySelect, cities)
        if (afterCityUpdate) {
          afterCityUpdate()
        }
      } catch (error) {
        console.error('市区町村の取得エラー:', error)
        this.showError('市区町村データの取得に失敗しました')
        this.resetCityOptions(citySelect) // エラー時はリセット
      }
    }, 300)
  }

  // 都道府県変更時の市区町村動的取得
  async changePrefecture (event) {
    const prefectureId = event.target.value
    const citySelect = this.residentCityIdTarget

    await this.handlePrefectureChange(prefectureId, citySelect, 'prefectureChangeTimeout')
  }

  // 市区町村選択肢をリセット
  resetCityOptions (citySelect) {
    citySelect.innerHTML = ''
    const emptyOption = document.createElement('option')
    emptyOption.value = ''
    emptyOption.textContent = ''
    citySelect.appendChild(emptyOption)
  }

  // 市区町村ローディング状態を表示
  showCityLoading (citySelect) {
    citySelect.innerHTML = ''
    const emptyOption = document.createElement('option')
    emptyOption.value = ''
    emptyOption.textContent = ''
    citySelect.appendChild(emptyOption)
  }

  // 都道府県の市区町村データを取得
  async fetchCitiesForPrefecture (prefectureId) {
    const response = await fetch(`/area/prefectures/${prefectureId}/cities`)

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    return await response.json()
  }

  // 市区町村選択肢を設定
  populateCityOptions (citySelect, cities) {
    citySelect.innerHTML = ''
    // 空のoptionを最初に追加
    const emptyOption = document.createElement('option')
    emptyOption.value = ''
    emptyOption.textContent = ''
    citySelect.appendChild(emptyOption)

    cities.forEach(city => {
      const option = document.createElement('option')
      option.value = city.id
      option.textContent = city.name
      citySelect.appendChild(option)
    })
  }

  // エリア検索用都道府県変更時の市区町村動的取得
  async changeAreaSearchPrefecture (event) {
    const prefectureId = event.target.value
    const citySelect = this.areaSearchCityIdTarget

    await this.handlePrefectureChange(
      prefectureId,
      citySelect,
      'areaSearchPrefectureChangeTimeout',
      () => this.updateAreaCenterAddressFromSelects(),
    )
  }

  // エリア検索用市区町村変更時の中心地住所更新
  changeAreaSearchCity (event) {
    // 中心地住所を更新
    this.updateAreaCenterAddressFromSelects()
  }

  // 番地以降の入力時の中心地住所更新
  updateAreaDetailAddress (event) {
    this.updateAreaCenterAddressFromSelects()
  }

  // STEP3

  // 中心地住所を取得（エリア指定検索のみ）
  getCurrentCenterAddress () {
    return this.areaCenterAddressTarget.value
  }

  // セレクトボックスと番地入力の値から中心地住所を更新
  updateAreaCenterAddressFromSelects () {
    const prefectureSelect = this.areaSearchPrefectureIdTarget
    const citySelect = this.areaSearchCityIdTarget
    const detailAddress = this.hasAreaDetailAddressTarget ? this.areaDetailAddressTarget.value : ''

    let address = ''

    if (prefectureSelect.value) {
      const selectedPrefectureOption = prefectureSelect.options[prefectureSelect.selectedIndex]
      address += selectedPrefectureOption.text

      if (citySelect.value) {
        const selectedCityOption = citySelect.options[citySelect.selectedIndex]
        address += selectedCityOption.text
      }
    }

    // 番地以降が入力されている場合は追加
    if (detailAddress && detailAddress.trim()) {
      address += detailAddress.trim()
    }

    this.areaCenterAddressTarget.value = address

    // 地図表示ボタンの有効/無効を更新
    this.updateShowMapButtonAvailability()
  }

  // 地図表示ボタンの有効/無効を制御
  updateShowMapButtonAvailability () {
    const prefectureSelect = this.areaSearchPrefectureIdTarget
    const citySelect = this.areaSearchCityIdTarget

    // 都道府県と市区町村の両方が選択されている場合のみボタンを有効にする
    const isEnabled = prefectureSelect.value && citySelect.value

    if (this.hasShowMapButtonTarget) {
      this.showMapButtonTarget.disabled = !isEnabled

      if (isEnabled) {
        this.showMapButtonTarget.classList.remove('btn-secondary')
        this.showMapButtonTarget.classList.add('btn-primary')
      } else {
        this.showMapButtonTarget.classList.remove('btn-primary')
        this.showMapButtonTarget.classList.add('btn-secondary')
      }
    }
  }
}
